import React, { useState, useEffect } from 'react';
import { read, utils, write } from 'xlsx';
import { getSignupDetailsForCust, getSubscriptionDetails } from '../fetch';
import { useMsal } from "@azure/msal-react";
import { Spinner, Button, Form, Modal } from 'react-bootstrap';
import { Redirect } from 'react-router-dom';

export const csInfo = () => {
    // Initialize state with values from localStorage if available
    const [fileData, setFileData] = useState(() => {
        const savedData = localStorage.getItem('customerTableData');
        return savedData ? JSON.parse(savedData) : [];
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [redirectToDetails, setRedirectToDetails] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const { instance } = useMsal();
    const currentAccount = instance.getActiveAccount();

    // Define all available columns, retrieve from localStorage if available
    const [availableColumns, setAvailableColumns] = useState(() => {
        const savedColumns = localStorage.getItem('customerTableColumns');
        if (savedColumns) {
            return JSON.parse(savedColumns);
        }
        
        // Default columns configuration
        return {
            // Customer Data
            client_url: { selected: true, label: 'Client URL', category: 'Customer' },
            created_date: { selected: true, label: 'Created Date', category: 'Customer' },
            product_package_name: { selected: true, label: 'Package Name', category: 'Customer' },
            contact_email: { selected: true, label: 'Contact Email', category: 'Customer' },
            user_count: { selected: true, label: 'User Count', category: 'Customer' },
            account_type: { selected: false, label: 'Account Type', category: 'Customer' },
            allow_stakeholders: { selected: false, label: 'Allow Stakeholders', category: 'Customer' },
            allow_live_call_routing: { selected: false, label: 'Allow Live Call Routing', category: 'Customer' },
            expiration_date: { selected: false, label: 'Expiration Date', category: 'Customer' },
            pending_verification: { selected: false, label: 'Pending Verification', category: 'Customer' },
            pending_activation: { selected: false, label: 'Pending Activation', category: 'Customer' },
            timezone: { selected: false, label: 'Timezone', category: 'Customer' },
            accounturl: { selected: false, label: 'Account URL', category: 'Customer' },
            sales_person_name: { selected: false, label: 'Sales Person', category: 'Customer' },
            cs_person_name: { selected: false, label: 'CS Person', category: 'Customer' },
            activity_index: { selected: false, label: 'Activity Index', category: 'Customer' },
            contact_name: { selected: false, label: 'Contact Name', category: 'Customer' },
            contact_phone: { selected: false, label: 'Contact Phone', category: 'Customer' },
            last_login_date_cst: { selected: false, label: 'Last Login Date', category: 'Customer' },
            stakeholder_count: { selected: false, label: 'Stakeholder Count', category: 'Customer' },
            groups_count: { selected: false, label: 'Groups Count', category: 'Customer' },
            schedulecount: { selected: false, label: 'Schedule Count', category: 'Customer' },
            alerts_count: { selected: false, label: 'Alerts Count', category: 'Customer' },
            inbound_rule_count: { selected: false, label: 'Inbound Rule Count', category: 'Customer' },
            inbound_messg_count: { selected: false, label: 'Inbound Message Count', category: 'Customer' },
            outboundsvccount: { selected: false, label: 'Outbound Service Count', category: 'Customer' },
            last_alert_created_date: { selected: false, label: 'Last Alert Created Date', category: 'Customer' },
            inbound_integration_names: { selected: false, label: 'Inbound Integration Names', category: 'Customer' },
            outbound_integration_names: { selected: false, label: 'Outbound Integration Names', category: 'Customer' },
            call_routing_count: { selected: false, label: 'Call Routing Count', category: 'Customer' },
            call_routingnames: { selected: false, label: 'Call Routing Names', category: 'Customer' },
            workflow_count: { selected: false, label: 'Workflow Count', category: 'Customer' },
            escalation_policy_count: { selected: false, label: 'Escalation Policy Count', category: 'Customer' },
            
            // Subscription Data
            paymentmethod: { selected: true, label: 'Payment Method', category: 'Subscription' },
            annualfee: { selected: true, label: 'Annual Fee', category: 'Subscription' },
            renewaldate: { selected: true, label: 'Renewal Date', category: 'Subscription' },
            last_payment_date: { selected: true, label: 'Last Payment Date', category: 'Subscription' },
            user_fee: { selected: false, label: 'User Fee', category: 'Subscription' },
            userlicenses: { selected: false, label: 'User Licenses', category: 'Subscription' },
            stakeholderfee: { selected: false, label: 'Stakeholder Fee', category: 'Subscription' },
            stakeholder_licenses: { selected: false, label: 'Stakeholder Licenses', category: 'Subscription' },
            call_routing_fee: { selected: false, label: 'Call Routing Fee', category: 'Subscription' },
            subscriptionstartdate: { selected: false, label: 'Subscription Start Date', category: 'Subscription' },
            expirationdate: { selected: false, label: 'Subscription Expiration Date', category: 'Subscription' },
            productpackageid: { selected: false, label: 'Product Package ID', category: 'Subscription' },
            licensetermtypeid: { selected: false, label: 'License Term Type ID', category: 'Subscription' },
            license_term_type: { selected: false, label: 'License Term Type', category: 'Subscription' },
            effectivedate: { selected: false, label: 'Effective Date', category: 'Subscription' },
            comments: { selected: false, label: 'Comments', category: 'Subscription' },
            call_routing: { selected: false, label: 'Call Routing Enabled', category: 'Subscription' },
            stakeholders: { selected: false, label: 'Stakeholders Enabled', category: 'Subscription' }
        };
    });

    // Save state to localStorage whenever it changes
    useEffect(() => {
        if (fileData.length > 0) {
            localStorage.setItem('customerTableData', JSON.stringify(fileData));
        }
    }, [fileData]);

    useEffect(() => {
        localStorage.setItem('customerTableColumns', JSON.stringify(availableColumns));
    }, [availableColumns]);

    // Process file upload
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        setUploadedFile(file);
        // Don't clear previous data when uploading a new file
        // This allows users to upload multiple files and see all data
    };

    // Clear saved data
    const handleClearData = () => {
        setFileData([]);
        localStorage.removeItem('customerTableData');
    };

    // Toggle column selection
    const toggleColumn = (columnName) => {
        setAvailableColumns({
            ...availableColumns,
            [columnName]: {
                ...availableColumns[columnName],
                selected: !availableColumns[columnName].selected
            }
        });
    };

    // Select all columns in a category
    const selectAllInCategory = (category, isSelected) => {
        const updatedColumns = { ...availableColumns };
        
        Object.keys(updatedColumns).forEach(key => {
            if (updatedColumns[key].category === category) {
                updatedColumns[key].selected = isSelected;
            }
        });
        
        setAvailableColumns(updatedColumns);
    };

    // Get selected columns
    const getSelectedColumns = () => {
        return Object.keys(availableColumns).filter(key => availableColumns[key].selected);
    };

    // Handle Get Data button click
    const handleGetData = async () => {
        if (!uploadedFile) {
            alert('Please upload a file first');
            return;
        }
        setIsLoading(true);

        try {
            const data = await readFile(uploadedFile);
            
            // Validate that the file has at least 1 row of data
            if (data.length === 0) {
                alert('Error: Your file appears to be empty or has no valid data rows. Please check your file format and try again.');
                setIsLoading(false);
                return;
            }
            
            // Check if file has more than 6 rows and show warning
            let dataToProcess = data;
            if (data.length > 6) {
                alert(`Warning: Your file contains ${data.length} entries. Only the first 6 entries will be processed.`);
                dataToProcess = data.slice(0, 6);
            }
            
            // Process the data (up to 6 rows)
            const processedData = await processClientUrls(dataToProcess);
            
            // Append new data to existing data instead of replacing
            setFileData(prevData => [...prevData, ...processedData]);
        } catch (error) {
            console.error("Error processing file:", error);
            alert('Error processing file. Please check the file format and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle row click
    const handleRowClick = (account) => {
        setSelectedAccount(account);
        setRedirectToDetails(true);
    };

    // Export data to Excel
    const handleExportToExcel = () => {
        if (fileData.length === 0) {
            alert('No data to export');
            return;
        }

        // Filter data to only include selected columns
        const selectedColumns = getSelectedColumns();
        const filteredData = fileData.map(row => {
            const filteredRow = {};
            selectedColumns.forEach(column => {
                filteredRow[availableColumns[column].label] = row[column];
            });
            return filteredRow;
        });

        // Create a new workbook
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Customer Data");

        // Generate Excel file and trigger download
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'customer_data.xlsx';
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);
    };

    // Read XLSX/CSV file
    const readFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const workbook = read(e.target.result, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const data = utils.sheet_to_json(firstSheet);
                    resolve(data);
                } catch (error) {
                    reject(error);
                }
            };
            reader.readAsArrayBuffer(file);
        });
    };

    // Process each client URL
    const processClientUrls = async (rows) => {
        const processedData = [];

        for (const row of rows) {
            const clientUrl = row.ClientURL;
            try {
                // Get customer details
                const customerResponse = await getSignupDetailsForCust(clientUrl);
                if (customerResponse.status !== 200 && customerResponse.status !== 201) {
                    continue;
                }
                const customerData = customerResponse.data[0];

                // Get subscription details
                const subscriptionResponse = await getSubscriptionDetails(clientUrl);
                if (subscriptionResponse.status !== 200 && subscriptionResponse.status !== 201) {
                    continue;
                }
                const subscriptionData = subscriptionResponse.data[0];

                // Combine all data fields from both APIs
                const combinedData = {
                    // Customer data fields
                    client_url: customerData.client_url,
                    created_date: customerData.created_date,
                    product_package_name: customerData.product_package_name,
                    contact_email: customerData.contact_email,
                    user_count: customerData.user_count,
                    account_type: customerData.account_type,
                    allow_stakeholders: customerData.allow_stakeholders,
                    allow_live_call_routing: customerData.allow_live_call_routing,
                    expiration_date: customerData.expiration_date,
                    pending_verification: customerData.pending_verification,
                    pending_activation: customerData.pending_activation,
                    timezone: customerData.timezone,
                    accounturl: customerData.accounturl,
                    sales_person_name: customerData.sales_person_name,
                    cs_person_name: customerData.cs_person_name,
                    activity_index: customerData.activity_index,
                    contact_name: customerData.contact_name,
                    contact_phone: customerData.contact_phone,
                    last_login_date_cst: customerData.last_login_date_cst,
                    stakeholder_count: customerData.stakeholder_count,
                    groups_count: customerData.groups_count,
                    schedulecount: customerData.schedulecount,
                    alerts_count: customerData.alerts_count,
                    inbound_rule_count: customerData.inbound_rule_count,
                    inbound_messg_count: customerData.inbound_messg_count,
                    outboundsvccount: customerData.outboundsvccount,
                    last_alert_created_date: customerData.last_alert_created_date,
                    inbound_integration_names: customerData.inbound_integration_names,
                    outbound_integration_names: customerData.outbound_integration_names,
                    call_routing_count: customerData.call_routing_count,
                    call_routingnames: customerData.call_routingnames,
                    workflow_count: customerData.workflow_count,
                    escalation_policy_count: customerData.escalation_policy_count,

                    // Subscription data fields
                    paymentmethod: subscriptionData.paymentmethod,
                    annualfee: subscriptionData.annualfee,
                    renewaldate: subscriptionData.renewaldate,
                    last_payment_date: subscriptionData.last_payment_date,
                    user_fee: subscriptionData.user_fee,
                    userlicenses: subscriptionData.userlicenses,
                    stakeholderfee: subscriptionData.stakeholderfee,
                    stakeholder_licenses: subscriptionData.stakeholder_licenses,
                    call_routing_fee: subscriptionData.call_routing_fee,
                    subscriptionstartdate: subscriptionData.subscriptionstartdate,
                    expirationdate: subscriptionData.expirationdate,
                    productpackageid: subscriptionData.productpackageid,
                    licensetermtypeid: subscriptionData.licensetermtypeid,
                    license_term_type: subscriptionData.license_term_type,
                    effectivedate: subscriptionData.effectivedate,
                    comments: subscriptionData.comments,
                    call_routing: subscriptionData.call_routing,
                    stakeholders: subscriptionData.stakeholders
                };

                processedData.push(combinedData);
            } catch (error) {
                console.error(`Error processing ${clientUrl}:`, error);
            }
        }

        return processedData;
    };

    // Add this function before the return statement
    const downloadSampleTemplate = () => {
        // Create a simple workbook with ClientURL column
        const ws = utils.aoa_to_sheet([
            ['ClientURL'],
            ['client1'],
            ['client2'],
            ['client3'],
            ['client4'],
            ['client5'],
            ['client6']
        ]);
        
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Sample");
        
        // Generate Excel file and trigger download
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'sample_template.xlsx';
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);
    };

    if (redirectToDetails) {
        return <Redirect to={`/signupdetails?account=${encodeURIComponent(selectedAccount)}`} />;
    }

    return (
        <div className="container mt-5">
            <div className="row justify-content-center mb-5">
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-body">
                            <h4 className="card-title text-center mb-4">Upload Customer Data</h4>
                            
                            {/* Add help text section with info icon */}
                            <div className="alert alert-info mb-3" role="alert">
                                <div className="d-flex">
                                    <span style={{ fontSize: '1.2rem', marginRight: '10px' }}>ℹ️</span>
                                    <div>
                                        <strong>Required Format:</strong> Upload an Excel file with a column header named "ClientURL". 
                                        Each row should contain a client URL in this column.
                                        <br /><strong>Limit:</strong> Maximum 6 entries allowed per upload.
                                        <div className="mt-2">
                                            <button 
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => downloadSampleTemplate()}
                                            >
                                                Download Sample Template
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mb-4">
                                <input
                                    type="file"
                                    accept=".xlsx,.csv"
                                    onChange={handleFileUpload}
                                    className="form-control"
                                />
                            </div>
                            <div className="text-center">
                                <button 
                                    onClick={handleGetData}
                                    className="btn btn-primary"
                                    disabled={!uploadedFile || isLoading}
                                    style={{marginRight: '10px'}}
                                >
                                    {isLoading ? 'Loading...' : 'Get Data'}
                                </button>
                                <button 
                                    onClick={() => setShowColumnSelector(true)}
                                    className="btn btn-secondary"
                                    disabled={isLoading}
                                    style={{marginRight: '10px'}}
                                >
                                    Select Columns
                                </button>
                                {fileData.length > 0 && (
                                    <button 
                                        onClick={handleClearData}
                                        className="btn btn-danger"
                                    >
                                        Clear Data
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center mt-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden"></span>
                    </Spinner>
                </div>
            ) : (
                fileData.length > 0 && (
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">Customer Data ({fileData.length} records)</h5>
                                <Button 
                                    variant="success" 
                                    onClick={handleExportToExcel}
                                >
                                    Export to Excel
                                </Button>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            {getSelectedColumns().map(column => (
                                                <th key={column}>{availableColumns[column].label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fileData.map((row, index) => (
                                            <tr 
                                                key={index}
                                                onClick={() => handleRowClick(row.client_url)}
                                                style={{ cursor: 'pointer' }}
                                                className="table-row-hover"
                                            >
                                                {getSelectedColumns().map(column => (
                                                    <td key={column}>{
                                                        column === 'annualfee' || column === 'user_fee' || column === 'stakeholderfee' || column === 'call_routing_fee' 
                                                            ? row[column] !== null ? `$${row[column]}` : 'N/A'
                                                            : row[column] !== null ? row[column] : 'N/A'
                                                    }</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )
            )}

            {/* Column Selector Modal */}
            <Modal 
                show={showColumnSelector} 
                onHide={() => setShowColumnSelector(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Select Columns to Display</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <h5>Customer Information</h5>
                        <div className="d-flex mb-2">
                            <Button 
                                size="sm" 
                                variant="outline-primary" 
                                className="me-2"
                                onClick={() => selectAllInCategory('Customer', true)}
                            >
                                Select All
                            </Button>
                            <Button 
                                size="sm" 
                                variant="outline-secondary"
                                onClick={() => selectAllInCategory('Customer', false)}
                            >
                                Deselect All
                            </Button>
                        </div>
                        <div className="row">
                            {Object.keys(availableColumns)
                                .filter(key => availableColumns[key].category === 'Customer')
                                .map(key => (
                                    <div key={key} className="col-md-4 mb-2">
                                        <Form.Check 
                                            type="checkbox"
                                            id={`check-${key}`}
                                            label={availableColumns[key].label}
                                            checked={availableColumns[key].selected}
                                            onChange={() => toggleColumn(key)}
                                        />
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    
                    <div>
                        <h5>Subscription Information</h5>
                        <div className="d-flex mb-2">
                            <Button 
                                size="sm" 
                                variant="outline-primary" 
                                className="me-2"
                                onClick={() => selectAllInCategory('Subscription', true)}
                            >
                                Select All
                            </Button>
                            <Button 
                                size="sm" 
                                variant="outline-secondary"
                                onClick={() => selectAllInCategory('Subscription', false)}
                            >
                                Deselect All
                            </Button>
                        </div>
                        <div className="row">
                            {Object.keys(availableColumns)
                                .filter(key => availableColumns[key].category === 'Subscription')
                                .map(key => (
                                    <div key={key} className="col-md-4 mb-2">
                                        <Form.Check 
                                            type="checkbox"
                                            id={`check-${key}`}
                                            label={availableColumns[key].label}
                                            checked={availableColumns[key].selected}
                                            onChange={() => toggleColumn(key)}
                                        />
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowColumnSelector(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={() => setShowColumnSelector(false)}>
                        Apply
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

//export default csInfo;
